import { DataKiParams } from '../../domain/dataKi';
import {
  MAIN_FIELD_TYPE,
  Option,
  QueryParameter,
  SearchCondition,
} from '../../domain/schemas';

/**
 * 選択されたメーカーコードリストと設定（settingsOptions）から
 * そのメーカーが所属するメーカーグループコードの一意配列を返す。
 *
 * 解決できたグループが1件もない場合は `undefined` を返す。
 */
export const getMakerGroupCodesForSelectedMakers = (
  selectedMakerCodes: string[] | undefined,
  settingsOptions?: SearchCondition
): string[] | undefined => {
  if (!selectedMakerCodes || selectedMakerCodes.length === 0) return undefined;

  const makers = settingsOptions?.makers ?? [];

  // 命令的な forEach を避け、Mapコンストラクタにエントリを渡して宣言的に生成する
  // 理由: 副作用を伴うループを排除し、不変性を担保するため
  const makerCodeToGroupCodeMap = new Map<string, string>(
    makers
      .filter((maker) => Boolean(maker.groupCd))
      .map((maker) => [maker.code, maker.groupCd ?? ''])
  );

  const groupCodes = selectedMakerCodes
    .map((code) => makerCodeToGroupCodeMap.get(code))
    .filter((groupCode): groupCode is string => Boolean(groupCode));

  const uniqueGroupCodes = Array.from(new Set(groupCodes));

  return uniqueGroupCodes.length > 0 ? uniqueGroupCodes : undefined;
};

/**
 * QueryParameter を元に mainField 固有の検索パラメータを組み立てるユーティリティ。
 * - makerGroups: 指定されたグループのみをセットする
 * - makers: 指定されたメーカーのみをセットする
 * - その他: queryParams.name をキーとし、指定された値を単一要素とする配列を作成する。
 *
 * @param makerOptions - メーカーの選択肢。undefined の場合は空配列として処理。
 */
export const buildMainFieldFilterParams = (
  queryParameter: QueryParameter,
  searchCondition: DataKiParams,
  makerOptions: Option[] | undefined
): Partial<DataKiParams> => {
  const value =
    queryParameter.value === null ? undefined : String(queryParameter.value);

  if (!value) return {};

  if (queryParameter.name === 'makerGroups') {
    const makerCodeToGroupCodeMap = new Map(
      (makerOptions ?? []).map((maker) => [maker.code, maker.groupCd])
    );

    const makerIntersection =
      searchCondition.makers !== undefined
        ? searchCondition.makers.filter(
            (makerCode) => makerCodeToGroupCodeMap.get(makerCode) === value
          )
        : undefined;

    const makersToSend =
      makerIntersection !== undefined && makerIntersection.length > 0
        ? makerIntersection
        : undefined;

    return {
      makerGroups: [value],
      ...(makersToSend !== undefined ? { makers: makersToSend } : {}),
    };
  }

  if (queryParameter.name === 'makers') {
    const makerOption = (makerOptions ?? []).find((m) => m.code === value);
    const isMakerWithoutGroup =
      makerOption !== undefined && !makerOption.groupCd;

    return {
      makers: [value],
      ...(isMakerWithoutGroup ? { makerGroups: [] } : {}),
    };
  }

  const specificParam: Partial<DataKiParams> = {
    [queryParameter.name]: [value],
  };

  return specificParam;
};

/**
 * ForSijiritu 用の分母パラメータを生成するユーティリティ
 * 理由: グラフ計算において「分母」として扱うべき特定のフィールドを抽出するため
 */
export const toForSijirituParams = (
  dataKiParams: DataKiParams
): Pick<
  DataKiParams,
  'kiListForSijiritu' | 'sisTypesForSijiritu' | 'makersForSijiritu'
> => ({
  kiListForSijiritu: dataKiParams.kiList,
  sisTypesForSijiritu: dataKiParams.sisTypes,
  makersForSijiritu: dataKiParams.makers,
});

/**
 * リクエスト側の mainField パラメータを組み立てるユーティリティ（推移グラフ用）
 *
 * - リクエスト（requestParams）に含まれる hallId / mainFieldId を base にして
 * searchParams.mainField に応じたキー（sisTypes / makerGroups / makers）を設定する
 *
 * @param routeParams リクエスト由来の識別子。mainFieldRowName があれば 2 段目行の種別に従い mainFieldId を割り当てる（グループ集計ONでメーカー行のとき mainFieldId はメーカーコードのため必須）
 * @param currentSearchCondition T 現在の検索条件
 * @returns Partial<Record<string, string[]>> API に渡すパラメータ
 */
export const buildRequestMainFieldParams = <T extends DataKiParams>(
  routeParams:
    | {
        hallId: string;
        mainFieldId: string;
        mainFieldRowName?: string;
      }
    | undefined,
  currentSearchCondition: T
): Partial<Record<string, string[]>> => {
  if (!routeParams) return {};

  const baseParams: Record<string, string[]> = {
    mainFieldId: [routeParams.mainFieldId],
    hallId: [routeParams.hallId],
  };

  const rowName = routeParams.mainFieldRowName;

  if (rowName === 'sisTypes') {
    return { ...baseParams, sisTypes: [routeParams.mainFieldId] };
  }
  if (rowName === 'makerGroups') {
    return { ...baseParams, makerGroups: [routeParams.mainFieldId] };
  }
  if (rowName === 'makers') {
    return { ...baseParams, makers: [routeParams.mainFieldId] };
  }

  if (currentSearchCondition.mainField === MAIN_FIELD_TYPE.TYPE) {
    return { ...baseParams, sisTypes: [routeParams.mainFieldId] };
  }
  if (currentSearchCondition.mainField === MAIN_FIELD_TYPE.MAKER_GROUP) {
    return { ...baseParams, makerGroups: [routeParams.mainFieldId] };
  }
  if (currentSearchCondition.mainField === MAIN_FIELD_TYPE.MAKER) {
    return { ...baseParams, makers: [routeParams.mainFieldId] };
  }

  return baseParams;
};

/**
 * mainField 固有のパラメータを組み立てるユーティリティ（汎用）
 *
 * @param paramName string クリックされたクエリの name
 * @param paramValue string | null クリックされたクエリの value
 * @param currentParams T 現在の検索条件
 * @returns Partial<Record<string, string[]>> 組み立てた mainField パラメータ
 */
export const buildMainFieldParams = <T extends DataKiParams>(
  paramName: string,
  paramValue: string | null,
  currentParams: T
): Partial<Record<string, string[]>> => {
  if (paramValue === 'null') {
    return {
      kiList: currentParams.kiList,
      sisTypes: currentParams.sisTypes,
      makers: currentParams.makers,
      makerGroups: currentParams.makerGroups,
    };
  }

  const isAverageRow =
    paramValue === 'kiTagAverage' ||
    paramValue === 'excludedKiTagAverage' ||
    (paramValue !== null && paramValue.includes('onlyOneKiTagAverage'));

  if (isAverageRow || paramValue === null) {
    return {};
  }

  if (paramName === 'makerGroups') {
    return { makerGroups: [paramValue] };
  }

  const resultParams: Record<string, string[]> = {
    [paramName]: [paramValue],
  };

  return resultParams;
};

/**
 * 貸玉・推移グラフ・台別のアイコンクリック時など、メイン行の QueryParameter に応じた絞り込みパラメータを組み立てる。
 *
 * @param makerOptions settingsOptions.makers。グループとメーカーの対応解決に使用する。
 */
export const buildMainFieldParamsForRowSelection = <T extends DataKiParams>(
  queryParameter: QueryParameter,
  searchCondition: T,
  makerOptions: Option[] | undefined
): Partial<Record<string, string[]>> => {
  const { name, value } = queryParameter;

  // 特殊な行（平均行やnull行）の判定
  const isSpecialRow =
    value === 'null' ||
    value === 'kiTagAverage' ||
    value === 'excludedKiTagAverage' ||
    (value !== null && value.includes('onlyOneKiTagAverage'));

  if (isSpecialRow) {
    return buildMainFieldParams(name, value, searchCondition);
  }

  if (value === null) {
    return {};
  }

  // 特定のフィルターパラメータを構築する
  // 理由: buildMainFieldFilterParams の戻り値を Record<string, string[]> 形式に合わせるため
  const filterParams = buildMainFieldFilterParams(
    queryParameter,
    searchCondition,
    makerOptions
  );
  const isStringArray = (v: unknown): v is string[] =>
    Array.isArray(v) && v.every((x) => typeof x === 'string');

  const result: Partial<Record<string, string[]>> = {};
  Object.entries(filterParams).forEach(([key, value]) => {
    if (isStringArray(value)) {
      result[key] = value;
    }
  });

  return result;
};
